import { useTranslation } from "react-i18next";
import type {
  MessageTypeName,
  PlatformTypeName,
  ReportMessageOptionsResponse,
} from "@multi-chat/shared";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { messageTypeLabelKey } from "./incoming-report-presentation.js";
import { ReportChannelSelect } from "./ReportChannelSelect.js";

const ALL = "__ALL__";

export type IncomingReportFilterValues = {
  platform?: PlatformTypeName;
  endpointId?: number;
  messageType?: MessageTypeName;
};

export function countIncomingActiveFilters(values: IncomingReportFilterValues): number {
  let count = 0;
  if (values.platform) count += 1;
  if (values.endpointId != null) count += 1;
  if (values.messageType) count += 1;
  return count;
}

export function IncomingReportFilters({
  options,
  values,
  onChange,
  idPrefix = "reports-incoming",
}: {
  options: ReportMessageOptionsResponse | null;
  values: IncomingReportFilterValues;
  onChange: (patch: Partial<IncomingReportFilterValues>) => void;
  idPrefix?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const platforms = options?.platforms ?? [];
  const endpoints = options?.endpoints ?? [];
  const messageTypes = options?.messageTypes ?? [];

  return (
    <div className="grid w-full min-w-0 gap-1.5" data-testid={`${idPrefix}-filters`}>
      <ReportChannelSelect
        value={values.platform}
        platforms={platforms}
        onChange={(platform) => onChange({ platform })}
        allLabel={t("reports.incoming.filters.allChannels")}
        channelLabel={t("reports.incoming.filters.channel")}
        labelForPlatform={(platform) => t(`reports.incoming.platforms.${platform}`)}
        ariaLabel={t("reports.incoming.filters.channel")}
        testId={`${idPrefix}-channel-filter`}
      />

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.incoming.filters.endpoint")}</span>
        <SimpleSelect
          value={values.endpointId != null ? String(values.endpointId) : ALL}
          onValueChange={(value) =>
            onChange({
              endpointId: value === ALL ? undefined : Number(value),
            })
          }
          ariaLabel={t("reports.incoming.filters.endpoint")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.incoming.filters.allEndpoints") },
            ...endpoints.map((endpoint) => ({
              value: String(endpoint.endpointId),
              label: endpoint.secondaryLabel
                ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
                : endpoint.displayLabel,
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.incoming.filters.messageType")}</span>
        <SimpleSelect
          value={values.messageType ?? ALL}
          onValueChange={(value) =>
            onChange({
              messageType: value === ALL ? undefined : (value as MessageTypeName),
            })
          }
          ariaLabel={t("reports.incoming.filters.messageType")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.incoming.filters.allMessageTypes") },
            ...messageTypes.map((messageType) => ({
              value: messageType,
              label: t(messageTypeLabelKey(messageType)),
            })),
          ]}
        />
      </label>
    </div>
  );
}
